import { useState, type ReactNode } from "react";
import type { Control, FieldValues, Path } from "react-hook-form";
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@components/ui/form";
import { Input } from "@components/ui/input";
import { Button } from "@components/ui/button";
import { ExternalLink, Pencil } from "lucide-react";
import { CurrencyInput } from "@shared-kernel/primary/shared/shadcn/ui/currency-input";
import { LongTextField, LongTextarea } from "@shared-kernel/primary/shared/long-text-field/long-text-field";
import { ExternalBatchRequestScheduleForm } from "@academy-context/primary/shared/trainings/training-schedule-form/external-batch/external-batch-request-schedule-form.content";
import { DocumentUploadList } from "@academy-context/primary/student/external-provider-batch-request/document-upload-list.content";
import type { Uploader } from "@shared-kernel/primary/shared/api-errors/use-field-upload";
import type { TrainingDetails } from "../types/provider-training-form.types";

interface TrainingScheduleDescription {
  title: string;
  subtitle: string;
}

interface TrainingDetailsFormSectionProps<TFormValues extends FieldValues & TrainingDetails> {
  control: Control<TFormValues>;
  now: Date;
  /** Upload of an attached document, by the screen's action hook */
  onUploadAttachment: Uploader;
  /** Optional description text shown under the description field label */
  descriptionHint?: string;
  /** Optional description text shown under the objective field label */
  objectiveHint?: string;
  /** Optional content to display before the objective field (e.g., Alert with examples) */
  objectiveExtraContent?: ReactNode;
  /** Placeholder text for description field */
  descriptionPlaceholder?: string;
  /** Placeholder text for objective field */
  objectivePlaceholder?: string;
  /** Optional description text shown under the price field label */
  priceHint?: string;
  /** Optional content to render after attachments (e.g., for provider-specific attachment UI) */
  attachmentsHeader?: ReactNode;
  /** Custom description for training schedule */
  trainingScheduleDescription?: TrainingScheduleDescription;
  /** When true, websiteUrl is displayed as readonly text with an edit button */
  websiteUrlReadOnly?: boolean;
}

const DEFAULT_TRAINING_SCHEDULE_DESCRIPTION: TrainingScheduleDescription = {
  title: "Merci de renseigner précisément les dates de chaque journée de masterclass.",
  subtitle:
    "Ces informations seront intégrées dans les contrats de sous-traitance entre notre structure et l'organisme de formation. Elles serviront également de référence pour les feuilles d'émargement par demi-journée, conformément aux exigences des financeurs.",
};

/**
 * Shared form section for training details fields.
 * Extracts the training fields used in both:
 * - RequestDataSection (provider portal)
 * - ExternalProviderBatchRequestForm (student form)
 */
export const TrainingDetailsFormSection = <TFormValues extends FieldValues & TrainingDetails>({
  control,
  now,
  onUploadAttachment,
  descriptionHint,
  objectiveHint,
  objectiveExtraContent,
  descriptionPlaceholder = "Description de la masterclass...",
  objectivePlaceholder = "Objectifs de la masterclass...",
  priceHint,
  attachmentsHeader,
  trainingScheduleDescription = DEFAULT_TRAINING_SCHEDULE_DESCRIPTION,
  websiteUrlReadOnly = false,
}: TrainingDetailsFormSectionProps<TFormValues>) => {
  const [isEditingWebsiteUrl, setIsEditingWebsiteUrl] = useState(false);
  return (
    <div className="space-y-4">
      <FormField
        control={control}
        name={"title" as Path<TFormValues>}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Titre de la masterclass</FormLabel>
            <FormControl>
              <Input type="text" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <LongTextField
        control={control}
        name={"description" as Path<TFormValues>}
        label="Description"
        description={descriptionHint}
        placeholder={descriptionPlaceholder}
      />
      {/* Pas de `LongTextField` ici : `objectiveExtraContent` s'insère entre la description et la
          saisie, et une Alert n'a rien à faire dans le <p> de `FormDescription`. */}
      <FormField
        control={control}
        name={"objective" as Path<TFormValues>}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Objectifs</FormLabel>
            {objectiveHint && <FormDescription>{objectiveHint}</FormDescription>}
            {objectiveExtraContent}
            <FormControl>
              <LongTextarea field={field} placeholder={objectivePlaceholder} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={control}
        name={"price" as Path<TFormValues>}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Prix (TTC)</FormLabel>
            {priceHint && <FormDescription>{priceHint}</FormDescription>}
            <FormControl>
              <CurrencyInput className="max-w-xs" value={field.value} onChange={field.onChange} onBlur={field.onBlur} name={field.name} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name={"websiteUrl" as Path<TFormValues>}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Lien site web</FormLabel>
            <FormDescription>Lien vers le site de la masterclass (optionnel).</FormDescription>
            {websiteUrlReadOnly && !isEditingWebsiteUrl ? (
              <div className="flex items-center gap-2">
                {field.value ? (
                  <a
                    href={field.value as string}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 break-all text-sm text-primary hover:underline"
                  >
                    <span className="min-w-0">{field.value as string}</span>
                    <ExternalLink className="size-3 shrink-0" />
                  </a>
                ) : (
                  <p className="text-sm text-muted-foreground">Aucun site</p>
                )}
                <Button type="button" variant="ghost" size="icon" className="size-7 shrink-0" onClick={() => setIsEditingWebsiteUrl(true)}>
                  <Pencil className="size-3.5" />
                </Button>
              </div>
            ) : (
              <FormControl>
                <Input type="url" placeholder="https://..." {...field} />
              </FormControl>
            )}
            <FormMessage />
          </FormItem>
        )}
      />

      <ExternalBatchRequestScheduleForm
        initialStartDate={now}
        now={now}
        numberOfMonthsDisplayed={2}
        description={trainingScheduleDescription}
      />

      {attachmentsHeader}
      <DocumentUploadList control={control} name="attachments" onUpload={onUploadAttachment} />
    </div>
  );
};
